import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

let openCount = 0

/**
 * Accessible modal: portal to <body>, the app root goes `inert` while open,
 * focus moves into the dialog on open, Tab is trapped, Escape closes, and
 * focus returns to the opener on close.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  wide,
}: {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  wide?: boolean
}) {
  const { t } = useTranslation()
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null
    const root = document.getElementById('root')
    openCount += 1
    if (root && openCount === 1) root.setAttribute('inert', '')

    // Initial focus: the first focusable control, else the panel itself.
    const panel = panelRef.current
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? panel)?.focus()

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return }
      if (e.key !== 'Tab' || !panel) return
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement)
      if (items.length === 0) { e.preventDefault(); panel.focus(); return }
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (e.shiftKey && (document.activeElement === firstEl || document.activeElement === panel)) { e.preventDefault(); lastEl.focus() }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      openCount = Math.max(0, openCount - 1)
      if (root && openCount === 0) root.removeAttribute('inert')
      const opener = openerRef.current
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus()
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-md"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={
          'max-h-[90vh] w-full animate-slide-up overflow-y-auto rounded-t-xl bg-card p-lg shadow-xl outline-none sm:rounded-xl ' +
          (wide ? 'max-w-2xl' : 'max-w-md')
        }
        onClick={(e) => e.stopPropagation()}
      >
        {title ? (
          <div className="mb-md flex items-center justify-between">
            <h2 id={titleId} className="text-h3 font-bold text-text">{title}</h2>
            <button
              onClick={onClose}
              aria-label={t('webapp.a11y.close')}
              className="rounded-full p-xs text-h4 text-text-muted hover:text-text"
            >
              ×
            </button>
          </div>
        ) : null}
        {children}
      </div>
    </div>,
    document.body,
  )
}
